import React, { useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Drawer, toast } from "@outpave/ds";
import { Button, Typography } from "@mui/material";
import { Stack } from "../layout/reusableLayouts";

import CardPolicyForm from "components/common/cards/CardPolicyForm";
import useCardPrograms, { type CardProgram } from "hooks/useCardPrograms";
import { GET_CARD_PROGRAM } from "graphql/cardProgram";
import { useCardPresetData } from "hooks/useCardPresetData";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { CardPreset, cardPresetFromPolicy } from "utils/cards/cardPresets";
import CardPresetTextBox from "./cards/CardPresetTextBox";
import CardProgramSelect from "./cards/CardProgramSelect";

interface CardPresetConfigDrawerProps {
  selectedId?: string;
  open: boolean;
  handleCloseDrawer: (isRefetchData?: boolean) => void;
  onCreatePresetSuccess?: (preset: CardPreset) => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const CardPresetConfigDrawer: React.FC<CardPresetConfigDrawerProps> = (
  props,
) => {
  const {
    selectedId,
    open,
    handleCloseDrawer,
    onCreatePresetSuccess,
    onAfterClose,
  } = props;

  const { t } = useTranslation();

  const {
    formikCardPreset,
    cardProgramId,
    setCardProgramId,
    presetChanged,
    resetCardPreset,
    specificLoadingStates,
    atLeastOneLimit,
    handleSubmitCardPreset,
  } = useCardPresetData({
    presetId: selectedId,
  });
  const {
    programs,
    activePrograms,
    multiple: multiplePrograms,
    loading: programsLoading,
    loaded: programsLoaded,
  } = useCardPrograms();
  // Whether to offer a program is known only once the programs are, so a preset waits for them to save.
  const programsPending = programsLoading && !programsLoaded;
  // A program that can no longer issue cards stays listed so it can be cleared. A removed one is not among the
  // company's programs, so it is read on its own.
  const listedProgram = programs.find(
    (program) => program.id === cardProgramId,
  );
  const { data: savedProgramData } = useQuery<{
    getCardProgram: CardProgram | null;
  }>(GET_CARD_PROGRAM, {
    variables: { id: cardProgramId },
    skip: !cardProgramId || !!listedProgram,
  });
  const savedProgram = listedProgram ?? savedProgramData?.getCardProgram;
  const programOptions =
    !savedProgram ||
    activePrograms.some((program) => program.id === savedProgram.id)
      ? activePrograms
      : [...activePrograms, savedProgram];

  // Set by a save, so the values reset once the drawer has slid out rather than while it is still on screen.
  const resetAfterClose = useRef(false);

  const handleSubmit = async () => {
    try {
      const result = await handleSubmitCardPreset();
      if (selectedId) {
        toast.success(t("Preset updated"));
      } else {
        toast.success(t("Preset added!"));
      }
      if (onCreatePresetSuccess) {
        onCreatePresetSuccess(
          cardPresetFromPolicy(
            formikCardPreset.values,
            result.data.addCardPreset.id,
            formikCardPreset.values.nameOfPreset ?? "",
            cardProgramId,
          ),
        );
      }
      resetAfterClose.current = true;
      handleCloseDrawer(true);
    } catch (error: unknown) {
      const graphQLErrors = (error as { graphQLErrors?: { message: string }[] })
        ?.graphQLErrors;
      toast.error(graphQLErrors?.[0]?.message ?? t("Please try again!"));
    }
  };

  useEffect(() => {
    if (open && !selectedId) {
      // ie: if we are adding a new card preset
      resetCardPreset();
    }
    if (open) {
      formikCardPreset.setTouched({}, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const busy =
    specificLoadingStates.addingCardPreset ||
    specificLoadingStates.updatingCardPreset;

  const { requestLeave, dialog } = useUnsavedChangesGuard(
    presetChanged,
    handleCloseDrawer,
  );

  // Every close path clears the touched fields, and a save its values, once the drawer has slid out.
  const handleClose = () => {
    if (busy) return;
    requestLeave();
  };
  const handleAfterClose = () => {
    formikCardPreset.setTouched({}, false);
    if (resetAfterClose.current) resetCardPreset();
    resetAfterClose.current = false;
    onAfterClose?.();
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        onAfterClose={handleAfterClose}
        title={t("Card Settings")}
        loading={specificLoadingStates.loadingCardPresetData || busy}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={handleClose}
              disabled={busy}
            >
              {t("Back")}
            </Button>

            <Button
              variant="primary-filled"
              disabled={
                !formikCardPreset.isValid ||
                !presetChanged ||
                specificLoadingStates.loadingCardPresetData ||
                busy ||
                programsPending ||
                !atLeastOneLimit
              }
              onClick={handleSubmit}
            >
              {t("Save")}
            </Button>
          </>
        }
      >
        {!specificLoadingStates.loadingCardPresetData && (
          <>
            <Stack gap="var(--space-6)">
              <Stack gap="var(--space-2)">
                <Typography variant="h5">
                  {!!selectedId ? t("Edit Preset") : t("Create Preset")}
                </Typography>
                <Typography variant="body1">
                  {t("PresetCardLimitDescription")}
                </Typography>
              </Stack>

              <CardPresetTextBox formik={formikCardPreset} />
              {(multiplePrograms || !!cardProgramId) && (
                <CardProgramSelect
                  programs={programOptions}
                  value={cardProgramId}
                  onChange={setCardProgramId}
                  placeholder={t("Any card program")}
                />
              )}
            </Stack>
            <CardPolicyForm formik={formikCardPreset} variant="preset" />
          </>
        )}
      </Drawer>
      {dialog}
    </>
  );
};

export default CardPresetConfigDrawer;
